// @ts-nocheck

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

class CartPricing {
  constructor() {
    // Active presentment currency. Resolution order:
    // explicit config → Shopify's active currency → EUR.
    this.currency = window.cartPricingConfig?.currency
      || (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active)
      || 'EUR';

    // BCP-47 locale used for currency/number formatting (Intl.NumberFormat).
    this.locale = window.cartPricingConfig?.locale
      || (typeof Shopify !== 'undefined' && Shopify.locale)
      || 'fr';

    // Per-market config. Each market's affiliate tier ladder is denominated in
    // its OWN presentment currency: EUR for the Eurozone, and a hardcoded SAR
    // price ladder for Saudi Arabia (merchant "HC CTC" price sheet). Shopify's
    // /cart.js returns totals in the presentment currency, so tiers are matched
    // directly with no FX math — native markets use rate = 1. Any currency that
    // isn't configured falls back to the EUR ladder converted by Shopify's live
    // rate. The same offer URLs serve every market (position-matched).
    const market = this.getMarket(this.currency);
    this.rate = market.native
      ? 1
      : (Number(
          window.cartPricingConfig?.rate
          ?? (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.rate)
          ?? 1
        ) || 1);

    const config = this.getConfig(market);
    this.language = config.language;
    this.maxCartValue = config.maxCartValue;
    this.tiers = config.tiers;
    this.texts = config.texts;
    this.fbPixelId = config.fbPixelId;
    this.ttPixelId = config.ttPixelId;
    this.googlePixelId = config.googlePixelId;
    this.googlePixelLabel = config.googlePixelLabel;
    this.snapchatPixelId = config.snapchatPixelId;
    this.trackingPixel = config.trackingPixel;

    // Direct pricing mode — defaults ON (affiliate-link redirect). Override via window.cartPricingConfig.directMode.
    this.directMode = window.cartPricingConfig?.directMode ?? true;

    // Store configuration (only used by the fallback URL).
    this.sourceId = window.cartPricingConfig?.sourceId || 'chrono-promo.com';
    this.logoUrl = window.cartPricingConfig?.logoUrl || '';
    this.faviconUrl = window.cartPricingConfig?.faviconUrl || '';
    this.shopColor = window.cartPricingConfig?.shopColor || '16181b';
    this.shopReviews = window.cartPricingConfig?.shopReviews || '';
    this.source = 'chrono-promo';
    this.shopifyStore = (typeof Shopify !== 'undefined' && Shopify.shop)
      ? Shopify.shop.replace('.myshopify.com', '')
      : 'chrono-promo';

    this._captureLandingParams();

    window.currentCurrency = this.currency;
    window.currentLanguage = this.language;

    this._injectStyles();
    this.init();

    console.log('✅ CartPricing initialized');
  }

  /* ── Persist landing-page tracking params so they survive navigation ── */
  _captureLandingParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const trackingParams = new URLSearchParams();
    const keys = ['source_id', 'sub1', 'tid', 'click_id', 'efid'];
    for (const key of keys) {
      const value = urlParams.get(key);
      if (value) trackingParams.set(key, value);
    }
    const paramsString = trackingParams.toString();
    if (paramsString) {
      document.cookie = `cp_ref=${encodeURIComponent(paramsString)}; path=/; max-age=86400; SameSite=Lax`;
      try { localStorage.setItem('cp_ref', paramsString); } catch (e) {}
      try { sessionStorage.setItem('cp_ref', paramsString); } catch (e) {}
      console.log('🍪 Landing params captured:', paramsString);
    }
  }

  _getLandingParams() {
    try {
      const localRaw = localStorage.getItem('cp_ref');
      if (localRaw) { console.log('💾 localStorage hit:', localRaw); return new URLSearchParams(localRaw); }
    } catch (e) {}
    try {
      const sessionRaw = sessionStorage.getItem('cp_ref');
      if (sessionRaw) { console.log('📦 sessionStorage hit:', sessionRaw); return new URLSearchParams(sessionRaw); }
    } catch (e) {}
    const cookieRaw = this.getCookieValue('cp_ref');
    if (cookieRaw) {
      try { const decoded = decodeURIComponent(cookieRaw); console.log('🍪 Cookie hit:', decoded); return new URLSearchParams(decoded); } catch (e) {}
    }
    const urlParams = new URLSearchParams(window.location.search);
    const trackingParams = new URLSearchParams();
    const keys = ['source_id', 'sub1', 'tid', 'click_id', 'efid'];
    for (const key of keys) { const value = urlParams.get(key); if (value) trackingParams.set(key, value); }
    if (trackingParams.toString()) console.log('🌐 URL fallback:', trackingParams.toString());
    return trackingParams;
  }

  _injectStyles() {
    const style = document.createElement('style');
    style.textContent = `
      .cart-updating-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.9);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 100;
        backdrop-filter: blur(4px);
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.2s ease;
      }
      .cart-updating-overlay.active {
        opacity: 1;
        pointer-events: all;
      }
      .cart-loader {
        width: 40px;
        height: 40px;
        border: 3px solid #f3f3f3;
        border-top: 3px solid #3f070e;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      .cart-shimmer {
        position: relative;
        overflow: hidden;
      }
      .cart-shimmer::after {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
        animation: shimmer 1.2s infinite;
      }
      @keyframes shimmer {
        0% { left: -100%; }
        100% { left: 100%; }
      }
      .cart-error-message {
        background: linear-gradient(135deg, #EF4135, #c0392b);
        border: 2px solid rgba(255, 255, 255, 0.3);
        color: white;
        padding: 1.25rem 2rem;
        border-radius: 12px;
        font-size: 1rem;
        font-weight: 600;
        text-align: center;
        position: fixed;
        top: 30px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 10001;
        box-shadow: 0 12px 32px rgba(239, 65, 53, 0.4), 0 4px 12px rgba(0, 0, 0, 0.15);
        animation: errorSlideDown 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        min-width: 320px;
        max-width: 90vw;
        backdrop-filter: blur(10px);
      }
      .cart-error-message::before {
        content: "⚠️ ";
        font-size: 1.2rem;
        margin-right: 0.5rem;
      }
      @keyframes errorSlideDown {
        from { opacity: 0; transform: translateX(-50%) translateY(-30px) scale(0.9); }
        to   { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
      }
      @keyframes errorSlideUp {
        from { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
        to   { opacity: 0; transform: translateX(-50%) translateY(-30px) scale(0.9); }
      }
      /* ── Cart button loading state ── */
      [data-cart-pricing-loading] {
        position: relative !important;
        pointer-events: none !important;
        cursor: not-allowed !important;
        overflow: hidden !important;
      }
      [data-cart-pricing-loading] > :not(.cart-pricing-spinner-el) {
        opacity: 0 !important;
        transition: opacity 0.15s ease;
      }
      .cart-pricing-spinner-el {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
      }
      .cart-pricing-spinner-el::before {
        content: '';
        width: 22px;
        height: 22px;
        border: 2.5px solid rgba(255, 255, 255, 0.3);
        border-top-color: #fff;
        border-radius: 50%;
        animation: spin 0.75s linear infinite;
      }

      /* ── Fullscreen redirect overlay ── */
      dialog.cart-pricing-overlay {
        border: none;
        padding: 0;
        margin: 0;
        max-width: 100vw;
        max-height: 100dvh;
        width: 100vw;
        height: 100dvh;
        background: transparent;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        animation: cartPricingOverlayIn 0.25s ease forwards;
      }
      dialog.cart-pricing-overlay::backdrop {
        background: rgba(0, 0, 0, 0.72);
        backdrop-filter: blur(6px);
        -webkit-backdrop-filter: blur(6px);
      }
      .cart-pricing-card {
        background: #fff;
        border-radius: 24px;
        padding: 44px 40px;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 18px;
        box-shadow: 0 32px 80px rgba(0, 0, 0, 0.35), 0 8px 24px rgba(0, 0, 0, 0.12);
        max-width: 320px;
        width: calc(100% - 48px);
        transform: translateY(28px) scale(0.95);
        animation: cartPricingCardIn 0.42s cubic-bezier(0.34, 1.4, 0.64, 1) forwards;
      }
      .cart-pricing-spinner {
        width: 52px;
        height: 52px;
        border: 3.5px solid #f0f0f0;
        border-top-color: #3f070e;
        border-radius: 50%;
        animation: spin 0.85s linear infinite;
        flex-shrink: 0;
      }
      .cart-pricing-title {
        margin: 0;
        font-size: 17px;
        font-weight: 700;
        color: #111827;
        line-height: 1.3;
        letter-spacing: -0.01em;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }
      .cart-pricing-sub {
        margin: 4px 0 0;
        font-size: 13px;
        color: #6b7280;
        font-weight: 400;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }
      .cart-pricing-trust {
        display: flex;
        align-items: center;
        gap: 5px;
        font-size: 12px;
        font-weight: 600;
        color: #059669;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }
      .cart-pricing-dot {
        display: inline-block;
        opacity: 0;
        animation: cartPricingDotFade 1.4s ease infinite;
      }
      .cart-pricing-dot:nth-child(2) { animation-delay: 0.2s; }
      .cart-pricing-dot:nth-child(3) { animation-delay: 0.4s; }
      @keyframes cartPricingDotFade {
        0%, 60%, 100% { opacity: 0; }
        30%            { opacity: 1; }
      }
      @keyframes cartPricingOverlayIn {
        to { opacity: 1; }
      }
      @keyframes cartPricingCardIn {
        to { transform: translateY(0) scale(1); }
      }
    `;
    document.head.appendChild(style);
  }

  // Global network: one redirect per entry price, accepted worldwide.
  getGlobalTiers() {
    return [
      { price: 200, url: 'https://www.molipy8trk.com/2HGSC85/WDQ52W4/' },
      { price: 295, url: 'https://www.molipy8trk.com/2HGSC85/WDRHXLP/' },
      { price: 395, url: 'https://www.molipy8trk.com/2HGSC85/WDSWSCB/' },
      { price: 495, url: 'https://www.molipy8trk.com/2HGSC85/WDW9P3X/' },
      { price: 595, url: 'https://www.molipy8trk.com/2HGSC85/WDXNKSJ/' },
      { price: 699, url: 'https://www.molipy8trk.com/2HGSC85/WF13GK5/' },
      { price: 795, url: 'https://www.molipy8trk.com/2HGSC85/WF2GC9Q/' },
      { price: 895, url: 'https://www.molipy8trk.com/2HGSC85/WF3T82C/' },
      { price: 999, url: 'https://www.molipy8trk.com/2HGSC85/WF584QZ/' },
      { price: 1190, url: 'https://www.molipy8trk.com/2HGSC85/WF6M1HK/' },
      { price: 1399, url: 'https://www.molipy8trk.com/2HGSC85/WF81W86/' },
      { price: 1495, url: 'https://www.molipy8trk.com/2HGSC85/WF9DQZR/' },
      { price: 1795, url: 'https://www.molipy8trk.com/2HGSC85/WFBRMPD/' },
      { price: 1999, url: 'https://www.molipy8trk.com/2HGSC85/WFFKF6L/' },
      { price: 2495, url: 'https://www.molipy8trk.com/2HGSC85/WFD6JG1/' },
      { price: 2995, url: 'https://www.molipy8trk.com/2HGSC85/WFGZ9X7/' },
      { price: 3999, url: 'https://www.molipy8trk.com/2HGSC85/WFJC6MS/' },
      { price: 4999, url: 'https://www.molipy8trk.com/2HGSC85/WFKQ3DF/' },
      { price: 5999, url: 'https://www.molipy8trk.com/2HGSC85/WFM4Z52/' },
      { price: 6999, url: 'https://www.molipy8trk.com/2HGSC85/WFNHSTM/' },
      { price: 7999, url: 'https://www.molipy8trk.com/2HGSC85/WFPWPL8/' },
      { price: 8999, url: 'https://www.molipy8trk.com/2HGSC85/WFR9LBT/' },
      { price: 9999, url: 'https://www.molipy8trk.com/2HGSC85/WFSNH3G/' },
    ];
  }

  // Country network: one redirect per country, single 9.74 entry price, and
  // the link only accepts traffic from that country.
  getCountryLinks() {
    return {
      FR: 'https://www.pv7gfn9trk.com/3HLZ7LT/LBGWTDB/',
      IT: 'https://www.pv7gfn9trk.com/3HLZ7LT/LBJ9Q4X/',
      SE: 'https://www.pv7gfn9trk.com/3HLZ7LT/LBKNLTJ/',
      CH: 'https://www.pv7gfn9trk.com/3HLZ7LT/LBXDS1R/',
      NL: 'https://www.pv7gfn9trk.com/3HLZ7LT/LBZRNQD/',
      BE: 'https://www.pv7gfn9trk.com/3HLZ7LT/LC26KH1/',
      DE: 'https://www.pv7gfn9trk.com/3HLZ7LT/LC3KG7L/',
    };
  }

  // Higher-ticket offers, per country. Same network as the country links but a
  // different parameter contract: sub6 is the product image and sub7 the
  // product name, so these must never fall through to the global map.
  getCountryOffers() {
    return {
      FR: {
        1999: 'https://www.pv7gfn9trk.com/3HLZ7LT/LF34RMW/',
        2999: 'https://www.pv7gfn9trk.com/3HLZ7LT/LF1PWX9/',
        4999: 'https://www.pv7gfn9trk.com/3HLZ7LT/LDZC26N/',
      },
    };
  }

  // Resolve the price ladder + cart cap for a presentment currency. Tiers are
  // {price (cents in that currency), url}. Prices are matched directly against
  // /cart.js totals (same currency) and displayed as-is (no FX) for `native`
  // markets. Unknown currencies fall back to the EUR ladder + live FX rate.
  getMarket(currency) {
    const global = this.getGlobalTiers();
    const tiers = global.map((t) => ({ price: t.price, url: t.url }));
    // 9.74 is the catalogue price and has no global price point: it resolves to
    // the country link where one exists at redirect time, and to the 9.99
    // global link everywhere else.
    const floor = global.find((t) => t.price === 999) || global[0];
    tiers.push({ price: 974, url: floor.url });
    tiers.sort((a, b) => a.price - b.price);
    const maxCartValue = 70000;
    const markets = {
      EUR: { tiers, maxCartValue, native: true },
    };
    return markets[currency] || { tiers, maxCartValue, native: false };
  }

  getConfig(market) {
    market = market || this.getMarket(this.currency);
    return {
      language: window.cartPricingConfig?.language || 'FR',
      // Cart cap in the active presentment currency (cents).
      maxCartValue: window.cartPricingConfig?.maxCartValue ?? market.maxCartValue,
      fbPixelId: window.cartPricingConfig?.fbPixelId || '',
      ttPixelId: window.cartPricingConfig?.ttPixelId || '',
      googlePixelId: window.cartPricingConfig?.googlePixelId || '',
      googlePixelLabel: window.cartPricingConfig?.googlePixelLabel || '',
      snapchatPixelId: window.cartPricingConfig?.snapchatPixelId || '',
      trackingPixel: window.cartPricingConfig?.trackingPixel || '',
      // Affiliate redirect tiers for the active market (presentment-currency
      // cents → shared offer URLs). See getMarket().
      tiers: window.cartPricingConfig?.tiers || market.tiers,
      // Customer-facing strings. FR defaults below are overridden by the
      // localized strings injected from Liquid (window.cartPricingConfig.texts),
      // which flow through Shopify Translate & Adapt for FR/EN/NL/DE.
      texts: Object.assign(
        {
          cartCheckout: 'COMMANDER',
          cartLimitError: 'Le panier est limité à {limit}',
          bundleDiscount: 'Remise Panier',
          redirectTitle: 'Redirection en cours',
          redirectSub: 'Vers le paiement sécurisé',
          redirectTrust: 'Paiement 100% sécurisé',
          checkoutError: '⚠️ Erreur de commande. Veuillez réessayer.',
          cartLabel: 'Panier',
        },
        window.cartPricingConfig?.texts || {}
      ),
    };
  }

  init() {
    this.initMetaPixel();
    this.initTikTokPixel();
    this.setupLoadingOverlay();
    this.setupCartEventListeners();
    this.setupCartRedirection();
    this.updateCartDisplay();
  }

  // Inject the standard Meta Pixel base code and fire PageView. The pixel id
  // flows in via window.cartPricingConfig.fbPixelId; no-ops if unset or already
  // loaded (e.g. a re-init or a pixel installed elsewhere).
  initMetaPixel() {
    if (!this.fbPixelId) return;
    if (window.fbq) {
      // Pixel runtime already present (e.g. Shopify's native Meta channel).
      // Only init our id if it isn't already registered — re-initializing an
      // existing pixel id triggers Meta's "Duplicate Pixel ID" warning and can
      // double-count events. If the internal registry isn't readable, fall
      // back to initializing (the prior behavior).
      try {
        var pixels = window.fbq.instance && window.fbq.instance.pixelsByID;
        var alreadyInit = pixels && pixels[this.fbPixelId];
        if (!alreadyInit) window.fbq('init', this.fbPixelId);
      } catch (e) { /* noop */ }
      return;
    }

    /* eslint-disable */
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
    (window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */

    window.fbq('init', this.fbPixelId);
    window.fbq('track', 'PageView');
    console.log(`📊 Meta Pixel initialized (${this.fbPixelId})`);
  }

  initTikTokPixel() {
    if (!this.ttPixelId) return;
    if (window.ttq && window.ttq._i && window.ttq._i[this.ttPixelId]) return;

    /* eslint-disable */
    !function (w, d, t) {
      w.TiktokAnalyticsObject = t;
      var ttq = w[t] = w[t] || [];
      ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie"];
      ttq.setAndDefer = function (t, e) { t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))) } };
      for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
      ttq.instance = function (t) { for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]); return e };
      ttq.load = function (e, n) {
        var i = "https://analytics.tiktok.com/i18n/pixel/events.js";
        ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = i;
        ttq._t = ttq._t || {}; ttq._t[e] = +new Date;
        ttq._o = ttq._o || {}; ttq._o[e] = n || {};
        var o = d.createElement("script"); o.type = "text/javascript"; o.async = !0; o.src = i + "?sdkid=" + e + "&lib=" + t;
        var a = d.getElementsByTagName("script")[0]; a.parentNode.insertBefore(o, a);
      };
    }(window, document, 'ttq');
    /* eslint-enable */

    window.ttq.load(this.ttPixelId);
    window.ttq.page();
  }

  setupLoadingOverlay() {
    const existing = document.querySelector('.cart-updating-overlay');
    if (existing) {
      this.loadingOverlay = existing;
      return;
    }
    const overlay = document.createElement('div');
    overlay.className = 'cart-updating-overlay';
    overlay.innerHTML = '<div class="cart-loader"></div>';
    this.loadingOverlay = overlay;
  }

  showLoading() {
    const cartSummary = document.querySelector('.sweep-cart__totals, .x-cart__footer, .cart-drawer__summary, .cart__summary-totals, .cart-summary');
    if (!cartSummary) return;
    if (!this.loadingOverlay.parentNode) {
      cartSummary.style.position = 'relative';
      cartSummary.appendChild(this.loadingOverlay);
    }
    this.loadingOverlay.classList.add('active');
    const bundleDiscount = document.querySelector('[data-bundle-discount]');
    const checkoutButton = document.querySelector('.sweep-cart__checkout-btn, .x-cart__checkout-btn, .cart__checkout-button');
    if (bundleDiscount) bundleDiscount.classList.add('cart-shimmer');
    if (checkoutButton) checkoutButton.classList.add('cart-shimmer');
  }

  hideLoading() {
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.remove('active');
    }
    document.querySelectorAll('.cart-shimmer').forEach(el => el.classList.remove('cart-shimmer'));
  }

  setupCartEventListeners() {
    const debouncedUpdate = debounce(() => this.updateCartDisplay(), 150);

    document.addEventListener('cart:update', async (e) => {
      const data = e.detail?.data;

      if (data?.source === 'product-form-component' && !data?.didError) {
        await this._validateCartLimit(e.detail?.sourceId, data?.itemCount);
      }

      debouncedUpdate();
    });

    // Refresh when cart drawer opens
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-x-cart-open]')) {
        debouncedUpdate();
      }
    });

    document.addEventListener('shopify:section:load', debouncedUpdate);
  }

  async _validateCartLimit(variantId, addedQty) {
    try {
      const cart = await this.getCart();
      if (cart.items_subtotal_price <= this.maxCartValue) return;

      let targetLine = cart.items.length;
      let prevQty = 0;

      if (variantId) {
        const idx = cart.items.findIndex(
          item => item.variant_id.toString() === variantId.toString()
        );
        if (idx >= 0) {
          targetLine = idx + 1;
          prevQty = Math.max(0, cart.items[idx].quantity - (addedQty || 1));
        }
      }

      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: targetLine, quantity: prevQty }),
      });

      // Show the cap in the active currency: the {limit} placeholder in the
      // localized string is replaced with the formatted maxCartValue.
      this.showError(this.texts.cartLimitError.replace('{limit}', this.formatPrice(this.maxCartValue)));

      document.dispatchEvent(new Event('cart:update', { bubbles: true }));
    } catch (err) {
      console.error('Cart limit validation error:', err);
    }
  }

  async getCart() {
    const response = await fetch('/cart.json');
    return response.json();
  }

  calculateBottomTier(totalAmount) {
    // Defensive: if tiers are ever cleared via config, charge the real cart
    // total with no tier remapping or bundle discount.
    if (!this.tiers || this.tiers.length === 0) {
      return { price: totalAmount };
    }
    let selectedTier = this.tiers[0];
    for (const tier of this.tiers) {
      if (tier.price <= totalAmount) {
        selectedTier = tier;
      } else {
        break;
      }
    }
    console.log(`💰 Cart total: ${this.formatPrice(totalAmount)} → Tier: ${this.formatPrice(selectedTier.price)}`);
    return selectedTier;
  }

  async calculateCartTotals() {
    try {
      const cart = await this.getCart();

      if (cart.item_count === 0) {
        return { originalTotal: 0, tierTotal: 0, discount: 0 };
      }

      // Defensive: tiers are denominated in the active presentment currency, so
      // /cart.js must report that same currency. If it doesn't (market/cookie
      // misconfig), the tier ladder would be matched against the wrong-currency
      // total — warn loudly so it's caught before a customer is mischarged.
      if (cart.currency && this.currency && cart.currency !== this.currency) {
        console.warn(`⚠️ Currency mismatch: cart is ${cart.currency} but tiers are ${this.currency}. Tier matching may be wrong.`);
      }

      const originalTotal = cart.items_subtotal_price;
      const tier = this.calculateBottomTier(originalTotal);
      const tierTotal = tier.price;
      const discount = originalTotal - tierTotal;

      return {
        originalTotal,
        tierTotal,
        discount: discount > 0 ? discount : 0,
      };
    } catch (error) {
      console.error('Error calculating cart totals:', error);
      return { originalTotal: 0, tierTotal: 0, discount: 0 };
    }
  }

  async updateCartDisplay() {
    try {
      this.showLoading();
      const totals = await this.calculateCartTotals();

      if (totals.tierTotal === 0) {
        this.hideLoading();
        return;
      }

      this.updateCartSummary(totals);
      this.updateCartButton(totals);

      setTimeout(() => this.hideLoading(), 100);
    } catch (error) {
      console.error('❌ Error updating cart display:', error);
      this.hideLoading();
    }
  }

  updateCartSummary(totals) {
    const bundleContainer = document.querySelector('[data-bundle-discount]');
    if (bundleContainer) {
      if (totals.discount > 0) {
        const discountAmount = bundleContainer.querySelector('[data-discount-amount]');
        const originalTotal = bundleContainer.querySelector('[data-original-total]');
        const discountDisplay = bundleContainer.querySelector('[data-discount-display]');

        if (discountAmount) discountAmount.textContent = `-${this.formatPrice(totals.discount)}`;
        if (originalTotal) originalTotal.textContent = this.formatPrice(totals.originalTotal);
        if (discountDisplay) discountDisplay.textContent = `-${this.formatPrice(totals.discount)}`;

        bundleContainer.style.display = 'block';
      } else {
        bundleContainer.style.display = 'none';
      }
    }

    this.updateTotalDisplays(totals);
  }

  updateTotalDisplays(totals) {
    const formatted = this.formatPrice(totals.tierTotal);

    const selectors = [
      '[data-sweep-total]',
      '[data-cart-total]',
      '[data-cart-subtotal]',
      '.x-cart__grand-total[data-x-total]',
    ];

    for (const sel of selectors) {
      document.querySelectorAll(sel).forEach(el => {
        // This theme renders the cart total inside a <text-component value="…">
        // that re-renders from its `value` attribute on cart updates. Update
        // the attribute too, otherwise the tier price flickers back to the
        // real subtotal after a cart change.
        if (el.hasAttribute('value')) el.setAttribute('value', formatted);
        el.textContent = formatted;
      });
    }

    console.log(`💰 Updated totals to: ${formatted}`);
    this.syncEntryPrice();
  }

  updateCartButton(totals) {
    const checkoutBtn = document.querySelector('.sweep-cart__checkout-btn, #checkout, .x-cart__checkout-btn, .cart__checkout-button');
    if (!checkoutBtn) return;
    checkoutBtn.innerHTML = `<span>${this.texts.cartCheckout} - ${this.formatPrice(totals.tierTotal)}</span>`;
  }

  _showPricingLoading(btn) {
    btn.setAttribute('data-cart-pricing-loading', '');
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;

    const spinnerEl = document.createElement('span');
    spinnerEl.className = 'cart-pricing-spinner-el';
    spinnerEl.setAttribute('aria-hidden', 'true');
    btn.appendChild(spinnerEl);

    this._pricingOverlayTimer = setTimeout(() => {
      const dialog = document.createElement('dialog');
      dialog.className = 'cart-pricing-overlay';
      dialog.setAttribute('aria-live', 'polite');
      dialog.innerHTML = `
        <div class="cart-pricing-card">
          <div class="cart-pricing-spinner"></div>
          <div>
            <p class="cart-pricing-title">${this.texts.redirectTitle}<span class="cart-pricing-dot">.</span><span class="cart-pricing-dot">.</span><span class="cart-pricing-dot">.</span></p>
            <p class="cart-pricing-sub">${this.texts.redirectSub}</p>
          </div>
          <div class="cart-pricing-trust">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            ${this.texts.redirectTrust}
          </div>
        </div>
      `;
      dialog.addEventListener('cancel', (e) => e.preventDefault());
      document.body.appendChild(dialog);
      dialog.showModal();
      this._pricingOverlayEl = dialog;
    }, 200);
  }

  _hidePricingLoading(btn) {
    clearTimeout(this._pricingOverlayTimer);
    if (this._pricingOverlayEl) {
      if (this._pricingOverlayEl.open) this._pricingOverlayEl.close();
      this._pricingOverlayEl.remove();
      this._pricingOverlayEl = null;
    }

    if (btn) {
      btn.querySelector('.cart-pricing-spinner-el')?.remove();
      btn.removeAttribute('data-cart-pricing-loading');
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
    }
  }

  // Every route to a checkout is funnelled through goToCheckout(). The guard
  // listens in the CAPTURE phase so nothing in the theme (or a theme app) can
  // act on a checkout click/submit before us — the customer is always sent to
  // the offer URL, never to the native Shopify checkout.
  setupCartRedirection() {
    const CHECKOUT_SELECTOR = [
      '.sweep-cart__checkout-btn',
      '.x-cart__checkout-btn',
      '.cart__checkout-button',
      '#checkout',
      '[name="checkout"]',
      'a[href*="/checkout"]',
      '[data-shopify="payment-button"]',
      '.shopify-payment-button',
      '.additional-checkout-buttons',
    ].join(', ');

    document.addEventListener('click', (e) => {
      const target = e.target && e.target.closest ? e.target.closest(CHECKOUT_SELECTOR) : null;
      if (!target) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      this.goToCheckout(target);
    }, true);

    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      const action = form.getAttribute('action') || '';
      if (action.includes('/checkout') || form.querySelector('[name="checkout"]')) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.goToCheckout(form.querySelector('[name="checkout"]'));
      }
    }, true);
  }

  async goToCheckout(btn) {
    if (btn && btn.getAttribute('data-cart-pricing-loading') !== null) return;
    if (btn) this._showPricingLoading(btn);

    try {
      const cart = await this.getCart();
      if (cart.item_count === 0) {
        this._hidePricingLoading(btn);
        return;
      }

      const originalTotal = cart.items_subtotal_price;
      const tier = this.calculateBottomTier(originalTotal);
      const totals = {
        originalTotal,
        tierTotal: tier.price,
        discount: Math.max(0, originalTotal - tier.price),
      };

      this.trackCartEvent(cart, totals);

      // On-site first checkout step: collect the delivery details so the
      // affiliate checkout opens already filled in. Skippable — a null result
      // means the customer chose to continue without filling it.
      //
      // The redirect spinner is a <dialog>, i.e. the browser's top layer, so
      // it would cover the step below. Park it while the form is open and
      // bring it back for the actual redirect.
      const hasPrefillStep =
        window.okzPrefillStep && typeof window.okzPrefillStep.open === 'function';
      if (hasPrefillStep) this._hidePricingLoading(btn);

      let prefill = null;
      if (hasPrefillStep) {
        prefill = await window.okzPrefillStep.open({ cart, totals });
        this._showPricingLoading(btn);
      }

      const destinationUrl = this.buildOfferUrl(totals, cart, prefill);

      if (!destinationUrl) {
        this._hidePricingLoading(btn);
        this.showError(this.texts.checkoutError);
        return;
      }


      this.logClick(destinationUrl, cart, totals, prefill);

      console.log('🚀 Redirecting to checkout:', destinationUrl);
      window.location.href = destinationUrl;
    } catch (error) {
      console.error('Error during checkout:', error);
      this._hidePricingLoading(btn);
      this.showError(this.texts.checkoutError);
    }
  }

  trackCartEvent(cart, totals) {
    // Meta Pixel — InitiateCheckout. Value is the tier price actually charged.
    try {
      if (this.fbPixelId && typeof window.fbq === 'function') {
        window.fbq('track', 'InitiateCheckout', {
          value: this._money(totals.tierTotal),
          currency: this.currency,
          num_items: cart.item_count,
          content_type: 'product',
          content_ids: cart.items.map(item => item.product_id),
          contents: cart.items.map(item => ({
            id: item.product_id,
            quantity: item.quantity,
            item_price: this._money(item.final_price),
          })),
        });
      }
    } catch (error) {
      console.warn('⚠️ Error firing Meta Pixel InitiateCheckout:', error);
    }

    // TikTok Pixel — InitiateCheckout. Same tier total as the Meta event above.
    try {
      if (this.ttPixelId && typeof window.ttq === 'object' && typeof window.ttq.track === 'function') {
        window.ttq.track('InitiateCheckout', {
          value: this._money(totals.tierTotal),
          currency: this.currency,
          content_type: 'product',
          contents: cart.items.map(item => ({
            content_id: String(item.product_id),
            content_name: item.product_title,
            quantity: item.quantity,
            price: this._money(item.final_price),
          })),
        });
      }
    } catch (error) {
      console.warn('⚠️ Error firing TikTok Pixel InitiateCheckout:', error);
    }

    try {
      if (typeof Shopify !== 'undefined' && Shopify.analytics?.publish) {
        Shopify.analytics.publish('reached_checkout', {
          cartToken: cart.token,
          totalPrice: this._money(totals.tierTotal),
          currency: this.currency,
          items: cart.items.map(item => ({
            productId: item.product_id,
            variantId: item.variant_id,
            quantity: item.quantity,
            price: this._money(item.final_price),
            title: item.product_title,
            variantTitle: item.variant_title || '',
          })),
        });
        return;
      }
      document.dispatchEvent(new CustomEvent('shopify:analytics:reached_checkout', {
        bubbles: true,
        detail: {
          cartToken: cart.token,
          totalPrice: this._money(totals.tierTotal),
          currency: this.currency,
          items: cart.items,
        },
      }));
      if (typeof window.dataLayer !== 'undefined') {
        window.dataLayer.push({
          event: 'reached_checkout',
          ecommerce: {
            checkout: { actionField: { step: 1 } },
            products: cart.items.map(item => ({
              id: item.variant_id,
              name: item.product_title,
              variant: item.variant_title || '',
              price: this._money(item.final_price),
              quantity: item.quantity,
            })),
            currency: this.currency,
            value: this._money(totals.tierTotal),
          },
        });
      }
    } catch (error) {
      console.warn('⚠️ Error tracking reached_checkout event:', error);
    }
  }
  // A checkout is "initiated" the moment we send the visitor to the offer.
  // Record it (with the email when the prefill step gave us one) so the
  // abandoned-checkout emails can be driven by real clicks and not guesses.
  logClick(destinationUrl, cart, totals, prefill) {
    try {
      let store = '';
      try {
        store = String((window.Shopify && window.Shopify.shop) || '').replace('.myshopify.com', '');
      } catch (error) {
        store = '';
      }
      if (!store) store = location.hostname.replace(/^www\./, '');
      const items = (cart && cart.items) || [];
      const units = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const country = (prefill && prefill.country) || this.entryCountry || '';
      const tierTotal = (totals && totals.tierTotal) || '';
      const payload = {
        store,
        country,
        tier: String(tierTotal || ''),
        units,
        product: items
          .map((item) => (item.quantity || 1) + 'x ' + (item.product_title || item.title || ''))
          .join(' | ')
          .slice(0, 200),
        email: (prefill && prefill.email) || '',
        cid: this._clickId(),
        destination: String(destinationUrl || '').slice(0, 300),
      };
      const body = JSON.stringify(payload);
      const url = 'https://okazyo-leads.vercel.app/api/click';
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
      } else {
        fetch(url, {
          method: 'POST',
          body,
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          keepalive: true,
          mode: 'cors',
        }).catch(() => {});
      }
    } catch (error) {}
  }

  _clickId() {
    try {
      let id = sessionStorage.getItem('okz_cid');
      if (!id) {
        id = (window.crypto && window.crypto.randomUUID)
          ? window.crypto.randomUUID()
          : 'c' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
        sessionStorage.setItem('okz_cid', id);
      }
      return id;
    } catch (error) {
      return '';
    }
  }


  buildOfferUrl(totals, cart, prefill = null) {
    const firstItem = cart.items[0];
    if (!firstItem) {
      console.warn('⚠️ No first item in cart');
      return null;
    }

    const landingParams = this._getLandingParams();
    const customer = cart.customer || {};
    const address = customer.default_address || {};

    // Data captured by the on-site first step wins over the cart customer,
    // which is empty for every guest (i.e. almost everyone).
    const pf = prefill || {};
    const details = {
      first: pf.first || customer.first_name || '',
      last: pf.last || customer.last_name || '',
      email: pf.email || customer.email || '',
      phone: pf.phone || customer.phone || '',
      address: pf.address || address.address1 || '',
      city: pf.city || address.city || '',
      zip: pf.zip || address.zip || '',
      country: (pf.country || address.country_code || '').toUpperCase(),
      state: pf.state || address.province_code || '',
    };

    // The country network covers a fixed set of countries at a single 9.74
    // entry price; every other tier goes to the global price ladder.
    const countryLink = this.getCountryLinks()[details.country] || '';
    const extraOffer = (this.getCountryOffers()[details.country] || {})[totals.tierTotal] || '';
    const tier = this.tiers.find((t) => t.price === totals.tierTotal);
    const useCountryLink = Boolean(countryLink) && totals.tierTotal === 974;
    const useExtraOffer = Boolean(extraOffer);
    const destinationUrl = useExtraOffer
      ? extraOffer
      : (useCountryLink ? countryLink : (tier ? tier.url : ''));

    if (!destinationUrl) {
      console.warn('⚠️ Direct checkout URL not configured for tier', totals.tierTotal);
      return null;
    }

    const params = new URLSearchParams();
    params.set('source_id', this.shopifyStore);

    const items = cart.items || [];
    const distinctProducts = new Set(items.map((item) => item.product_id));
    let productName;
    let productImage;
    if (distinctProducts.size > 1) {
      productName = this.texts.cartLabel;
      productImage = this.faviconUrl;
    } else {
      const quantity = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const baseName = firstItem.product_title || firstItem.title || '';
      productName = quantity > 1 ? `${quantity}x ${baseName}` : baseName;
      productImage = firstItem.featured_image?.url || firstItem.image || '';
    }
    params.set('adv1', productName);
    params.set('adv2', productImage);

    if (landingParams.get('sub1')) params.set('sub1', landingParams.get('sub1'));

    if (details.first)   params.set('first',   details.first);
    if (details.last)    params.set('last',    details.last);
    if (details.email)   params.set('email',   details.email);
    if (details.phone)   params.set('phone',   details.phone);
    if (details.address) params.set('address', details.address);
    if (details.city)    params.set('city',    details.city);
    if (details.zip)     params.set('zip',     details.zip);
    if (details.country) params.set('country', details.country);
    if (details.state)   params.set('state',   details.state);

    if (useExtraOffer) {
      // Higher-ticket offer: sub3=email, sub4=first, sub5=last, sub6=image,
      // sub7=product. There are no address or city slots here.
      if (details.email) params.set('sub3', details.email);
      if (details.first) params.set('sub4', details.first);
      if (details.last)  params.set('sub5', details.last);
      params.set('sub6', productImage || '');
      params.set('sub7', productName || '');
      // These links expose a single pixel slot called `fb`; the network maps
      // it to whatever pixel we send, so the TikTok id goes there until they
      // expose a dedicated slot.
      const offerPixel = this.fbPixelId || this.ttPixelId || '';
      if (offerPixel) params.set('fb', offerPixel);
    } else if (useCountryLink) {
      // Country network: sub3=email, sub4=first name, sub5=last name,
      // postcode=postcode, city=city. sub5 must stay the surname here — the
      // combined pixel string would be read as the last name.
      if (details.email) params.set('sub3', details.email);
      if (details.first) params.set('sub4', details.first);
      if (details.last)  params.set('sub5', details.last);
      if (details.zip)   params.set('postcode', details.zip);
      const countryPixel = this.fbPixelId || this.ttPixelId || '';
      if (countryPixel) params.set('fb', countryPixel);
    } else {
      // Global network: attribution slots plus the combined pixel string.
      if (details.first)   params.set('sub10', details.first);
      if (details.last)    params.set('adv3',  details.last);
      if (details.zip)     params.set('sub3',  details.zip);
      if (details.email)   params.set('sub4',  details.email);
      if (details.city)    params.set('sub6',  details.city);
      if (details.address) params.set('sub7',  details.address);
      if (details.phone)   params.set('sub8',  details.phone);

      const sub5Parts = [];
      if (this.googlePixelId) sub5Parts.push(`______${this.googlePixelId}___${this.googlePixelLabel}`);
      if (this.ttPixelId) sub5Parts.push(`___${this.ttPixelId}`);
      if (this.fbPixelId) sub5Parts.push(this.fbPixelId);
      if (this.snapchatPixelId) sub5Parts.push(`${'_'.repeat(21)}${this.snapchatPixelId}`);
      if (sub5Parts.length) params.set('sub5', sub5Parts.join(''));
    }

    // Our own click id rides along so the network's postback can be matched
    // back to the click that produced it (and to the email we captured).
    const clickId = this._clickId();
    if (clickId) {
      // A custom parameter survives every hop and is echoed back as {okzcid};
      // the sub slots are kept as a fallback for links that drop it.
      params.set('okzcid', clickId);
      params.set(useExtraOffer ? 'sub10' : 'sub2', clickId);
    }

    for (const key of ['tid', 'click_id', 'efid']) {
      const value = landingParams.get(key);
      if (value) params.set(key, value);
    }

    const separator = destinationUrl.includes('?') ? '&' : '?';
    const finalUrl = `${destinationUrl}${separator}${params.toString()}`;
    console.log('🚀 Direct checkout URL:', finalUrl);
    return finalUrl;
  }

  getCookieValue(name) {
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return match ? match[2] : null;
  }

  // Numeric amount in the active presentment currency (e.g. for analytics).
  // For native markets (EUR, SAR) this.rate is 1, so cents are already in the
  // display currency; for fallback currencies the EUR amount is FX-converted.
  _money(priceInCents) {
    return Number(((priceInCents / 100) * this.rate).toFixed(2));
  }

  // The country network covers a fixed set of countries at 9,74; every other
  // visitor is charged 9,99 at the redirect. Keep what we show equal to what
  // they pay: for a country with no 9,74 link, the entry price is shown as 9,99.
  geoCountry() {
    if (!this._geoPromise) {
      let cached = '';
      try {
        const raw = window.localStorage && window.localStorage.getItem('cp_geo');
        if (raw) {
          const saved = JSON.parse(raw);
          if (saved && saved.c && Date.now() - (saved.t || 0) < 6048e5) cached = saved.c;
        }
      } catch (e) { /* storage blocked */ }

      this._geoPromise = cached
        ? Promise.resolve(cached)
        : window
            .fetch('https://okazyo-leads.vercel.app/api/geo', { credentials: 'omit' })
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
              const code = data && typeof data.country === 'string' ? data.country.toUpperCase() : '';
              try {
                window.localStorage.setItem('cp_geo', JSON.stringify({ c: code, t: Date.now() }));
              } catch (e) { /* ignore */ }
              return code;
            })
            .catch(() => '');
    }
    return this._geoPromise;
  }

  syncEntryPrice(country) {
    if (country) this.entryCountry = country;
    if (!this.entryCountry) return;
    if (this.getCountryLinks()[this.entryCountry]) return;
    if (/bot|crawl|spider|slurp|bingpreview|lighthouse|headless|phantomjs/i.test(navigator.userAgent)) return;
    if (!this.tiers.some((tier) => tier.price === 974)) return;
    this._replaceEntryPrice(document.body);
  }

  _replaceEntryPrice(root) {
    if (!root) return;
    const nodes = [];
    const collect = (node) => {
      if (node.nodeType === 3) {
        if (node.nodeValue.indexOf('9,74') !== -1 || node.nodeValue.indexOf('9.74') !== -1) nodes.push(node);
        return;
      }
      if (node.nodeType !== 1) return;
      if (/^(SCRIPT|STYLE|NOSCRIPT|TEXTAREA|TEMPLATE|IFRAME|SELECT)$/.test(node.tagName)) return;
      for (let i = 0; i < node.childNodes.length; i += 1) collect(node.childNodes[i]);
    };
    collect(root);
    for (const node of nodes) {
      node.nodeValue = node.nodeValue.replace(/(^|[^0-9.,])9([.,])74(?!\d)/g, (match, before, sep) => `${before}9${sep}99`);
    }
  }

  formatPrice(priceInCents) {
    // priceInCents is already in the active presentment currency for native
    // markets (this.rate = 1); FX-converted only for fallback currencies.
    const amount = (priceInCents / 100) * this.rate;
    // Preserve the exact legacy EUR format (e.g. "99,99€") for the FR/Eurozone
    // markets; use locale-aware currency formatting for everything else (e.g.
    // SAR renders with the Arabic locale and the ر.س symbol).
    if (this.currency === 'EUR') {
      return `${amount.toFixed(2).replace('.', ',')}€`;
    }
    try {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
      }).format(amount);
    } catch (e) {
      return `${amount.toFixed(2)} ${this.currency}`;
    }
  }

  showError(message) {
    document.querySelectorAll('.cart-error-message').forEach(el => el.remove());
    const errorDiv = document.createElement('div');
    errorDiv.className = 'cart-error-message';
    errorDiv.innerHTML = `<span>${message}</span>`;
    document.body.appendChild(errorDiv);
    setTimeout(() => {
      errorDiv.style.animation = 'errorSlideUp 0.4s ease-out forwards';
      setTimeout(() => { if (errorDiv.parentNode) errorDiv.remove(); }, 400);
    }, 4000);
  }
}

function initializeCartPricing() {
  if (window.cartPricing) {
    window.cartPricing._captureLandingParams();
    return;
  }

  window.cartPricing = new CartPricing();
  window.cartPricing.geoCountry().then((country) => window.cartPricing.syncEntryPrice(country));

  window.addEventListener('pageshow', (event) => {
    if (event.persisted && window.cartPricing) {
      window.cartPricing._captureLandingParams();
      window.cartPricing.updateCartDisplay();
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCartPricing);
} else {
  initializeCartPricing();
}
